import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getSponsors, destroySponsor } from '../../../api/SponsorsAPI';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import PerPageBlock from '../../../components/common/PerPageBlock';
import AppToaster from '../../AppToaster';

const SponsorsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [sponsorsList, setSponsorsList] = useState({ data: [], last_page: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [triggerFetch, setTriggerFetch] = useState(false);
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});

    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success!', message: successMessage });
            navigate(location.pathname, { replace: true });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        setIsLoading(true);
        const filters = { page: currentPage, per_page: perPage, search_term: searchTermCompleted };
        getSponsors(filters, (response) => {
            setIsLoading(false);
            if (response.sponsors) {
                setSponsorsList(response.sponsors);
            }
        });
    }, [currentPage, perPage, searchTermCompleted, triggerFetch]);

    const handleAddClick = () => navigate('/club_portal/sponsors/create');
    const handleEditClick = (id) => navigate(`/club_portal/sponsors/edit/${id}`);

    const handleDeleteClick = (sponsor) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `<p>You are about to delete the sponsor:</p><strong>${sponsor.name}</strong><p>This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                destroySponsor(sponsor, (response) => {
                    if (response.success) {
                        Swal.fire('Deleted!', 'The sponsor has been deleted.', 'success');
                        setTriggerFetch(p => !p);
                    } else {
                        Swal.fire('Error!', response?.response?.data?.message || 'Could not delete the sponsor.', 'error');
                    }
                });
            }
        });
    };

    return (
        <main>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <div className="container-fluid">
                <AppBreadCrumb title="Sponsors" />
                <div className="row">
                    <div className="col-12">
                        <div className="tab-wrapper mb-3">
                            <ul className="tabs">
                                <li className="tab-link active">All Sponsors</li>
                                <li className="ms-md-auto">
                                    <div className="text-md-end d-flex flex-column flex-md-row align-items-md-center">
                                        <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                        <button className="text-nowrap btn btn-primary btn-sm mt-2 mt-md-0 ms-md-3" onClick={handleAddClick}>
                                            <i className="ti ti-plus f-s-18"></i> Add Sponsor
                                        </button>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <div className="content-wrapper">
                            <div className="d-flex m-md-3">
                                {sponsorsList.data.length > 0 && (
                                    <>
                                        <AppPaginator entityName="Sponsors" currentPage={currentPage} totalPages={sponsorsList.last_page} setCurrentPage={setCurrentPage} />
                                        <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                    </>
                                )}
                                {isLoading && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                            </div>
                            <div className="tabs-content active">
                                <div className="row">
                                    {!isLoading && (
                                        sponsorsList.data.length > 0 ? sponsorsList.data.map(sponsor => (
                                            <div className="col-md-6 col-xl-4 project-card" key={sponsor.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden bg-light-primary text-primary">
                                                                <i className="fas fa-handshake fa-lg"></i>
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600 txt-ellipsis-1">{sponsor.name}</h6>
                                                            </div>
                                                            <div className="dropdown">
                                                                <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    <li><a className="dropdown-item" onClick={() => handleEditClick(sponsor.id)}><i className="ti ti-edit text-success" /> Edit</a></li>
                                                                    <li><a className="dropdown-item delete-button" onClick={() => handleDeleteClick(sponsor)}><i className="ti ti-trash text-danger" /> Delete</a></li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="d-flex">
                                                            <div>
                                                                <h6 className="text-dark f-s-14">Contact: <span className="text-primary">{sponsor.contact_person || 'N/A'}</span></h6>
                                                                <h6 className="text-dark f-s-14">Phone: <span className="text-primary">{sponsor.phone_number || 'N/A'}</span></h6>
                                                            </div>
                                                            <div className="flex-grow-1 text-end">
                                                                <p className="f-w-500 text-secondary">Id</p>
                                                                <h6 className="f-w-600">{sponsor.id}</h6>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )) : (
                                            <div className="card mb-3">
                                                <div className="row g-0">
                                                    <div className="col-md-8"><div className="card-body"><h5 className="card-title">No Sponsors Found</h5><p className="card-text">Click "Add Sponsor" to record your club's sponsors.</p></div></div>
                                                    <div className="col-md-4"><img alt="No data illustration" className="img-fluid" src="/assets/images/blog-app/09.jpg" /></div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default SponsorsPage;

